/**
 * URLs for the Tasks workspace's sidebar: which board the list shows
 * (`?board=FOR`, absent for General — the param the page has always read) and
 * which management dialog is open over it (`?manage=import` and friends).
 *
 * The dialogs live on the tasks page; the sidebar only links to them, so a
 * dialog opens the same way from the sidebar, from a bookmark or after a back
 * button, and closing it drops the param again. Pure, no React, so a node
 * test can pin the shapes.
 */

import { GENERAL_BOARD_KEY, boardKeyFromParam } from "@/lib/task-boards"

export const TASKS_MANAGE_PARAM = "manage"

export const TASKS_SETTINGS_PATH = "/tasks/settings"

/** The email section of Tasks → Settings; the old Notifications dialog lands here. */
export const TASKS_EMAIL_ANCHOR = "email-notifications"
export const TASKS_EMAIL_SETTINGS_HREF = `${TASKS_SETTINGS_PATH}#${TASKS_EMAIL_ANCHOR}`

export type TasksManageAction = "sections" | "import" | "notifications"

const MANAGE_ACTIONS: TasksManageAction[] = ["sections", "import", "notifications"]

export function readManageAction(search: URLSearchParams): TasksManageAction | null {
  const value = search.get(TASKS_MANAGE_PARAM)
  return MANAGE_ACTIONS.includes(value as TasksManageAction) ? (value as TasksManageAction) : null
}

function withQuery(path: string, params: URLSearchParams): string {
  const query = params.toString()
  return query ? `${path}?${query}` : path
}

/** The list on one board. Leaves any open dialog behind. */
export function boardHref(key: string, search: string): string {
  const params = new URLSearchParams(search)
  params.delete(TASKS_MANAGE_PARAM)
  if (key === GENERAL_BOARD_KEY) params.delete("board")
  else params.set("board", key)
  return withQuery("/tasks", params)
}

/**
 * A management dialog over the current tasks view: the list or a task page
 * stays where it is. From anywhere else in the workspace (Projects) it opens
 * over the tasks list.
 */
export function manageHref(action: TasksManageAction, pathname: string, search: string): string {
  // Task emails are set on the settings page now; the param still redirects there.
  if (action === "notifications") return TASKS_EMAIL_SETTINGS_HREF
  // /tasks/settings is its own page; the dialogs live on the list and task pages.
  const onTasks = (pathname === "/tasks" || pathname.startsWith("/tasks/")) && pathname !== TASKS_SETTINGS_PATH
  const params = new URLSearchParams(onTasks ? search : "")
  params.set(TASKS_MANAGE_PARAM, action)
  return withQuery(onTasks ? pathname : "/tasks", params)
}

/** The board a tasks URL is on; General when it names none. */
export function boardKeyFromSearch(search: string): string {
  return boardKeyFromParam(new URLSearchParams(search).get("board"))
}
